import { useSubscription } from "@selector/shared";
import PubSub from "pubsub-js";

import { PubSubActions } from "@/global/pubsub-actions.js";

import { useError, useErrorHandler } from "../../../app-utils/graphql/useError.js";
import {
  type CreateRegionFlagMutationVariables,
  type DeleteRegionFlagsMutationVariables,
  type GetRegionFlagQueryVariables,
  type GetRegionFlagsQueryVariables,
  type UpdateRegionFlagMutationVariables,
  useCreateRegionFlagMutation,
  useDeleteRegionFlagsMutation,
  useGetRegionFlagQuery,
  useGetRegionFlagsQuery,
  useUpdateRegionFlagMutation,
} from "./regionFlag.gql.js";

// TODO: update the user preferences in case of changing the projects (we should have a project selector component)
// TODO: also reload the flags table data in case of change (useSubscription from the table)

export const useGetRegionFlag = (variables: GetRegionFlagQueryVariables) => {
  const { data, loading, error } = useGetRegionFlagQuery({ variables, skip: !variables?.flagId });
  void useError(error);

  return { data: data?.genomeViewer?.regionFlag, loading, error };
};

export const useGetRegionFlags = (variables: GetRegionFlagsQueryVariables) => {
  const { data, loading, error, refetch } = useGetRegionFlagsQuery({ variables, skip: !variables?.contigId });
  useSubscription(PubSubActions.REFETCH_REGION_FLAGS, () => {
    void refetch();
  });
  void useError(error);

  return { data: data?.contig, loading, error };
};

export const useCreateRegionFlag = () => {
  const [saveFlag, { loading }] = useCreateRegionFlagMutation();
  const handleError = useErrorHandler({ throwError: true });

  const action = async (variables: CreateRegionFlagMutationVariables) => {
    try {
      const result = await saveFlag({ variables });
      PubSub.publish(PubSubActions.REFETCH_REGION_FLAGS);
      return result.data?.createRegionFlag;
    } catch (error) {
      throw await handleError(error);
    }
  };

  return [action, { loading }];
};

export const useUpdateRegionFlag = () => {
  const [updateFlag, { loading }] = useUpdateRegionFlagMutation();
  const handleError = useErrorHandler({ throwError: true });

  const action = async (variables: UpdateRegionFlagMutationVariables) => {
    try {
      const result = await updateFlag({ variables });
      PubSub.publish(PubSubActions.REFETCH_REGION_FLAGS);
      return result.data?.updateRegionFlag;
    } catch (error) {
      throw await handleError(error);
    }
  };

  return [action, { loading }];
};

export const useDeleteRegionFlags = () => {
  const [deleteFlag, { loading }] = useDeleteRegionFlagsMutation();
  const handleError = useErrorHandler({ throwError: true });

  const action = async (variables: DeleteRegionFlagsMutationVariables) => {
    try {
      const result = await deleteFlag({ variables });
      PubSub.publish(PubSubActions.REFETCH_REGION_FLAGS);
      return result.data?.deleteRegionFlags;
    } catch (error) {
      throw await handleError(error);
    }
  };

  return [action, { loading }];
};
